import GtmProductImpressionClick from './gtmProductImpressionClickDelegator';
import { Events, GtmTrackingType } from '../constants';
import delegatorClassList from './gtmDelegatorRegistry';

class GtmTrackingManager {
	constructor(isTest) {
		this.isTest = isTest || false;
		this.delegators = this.generateDelegators();
		this.instanceId = `GtmTrackingManagerInstance-${Date.now()}`;
		this.setTimeoutCallbackTimer = 2000;
		this.dataLayerReadyMaxCount = 10;
		this.dataLayerReadyCounter = 0;
		this.dataLayerReadyMilliseconds = 200;
		this.run = this.run.bind(this);
		this.run();
	}

	addEventListener() {
		if (window.dataLayer) {
			document.querySelector('body').addEventListener(
				Events.CLICK,
				function (evt) {
					this.eventDelegator(evt);
				}.bind(this),
			);

			window.addEventListener(
				Events.LOAD,
				function (evt) {
					this.eventDelegator(evt, GtmTrackingType.PRODUCT_DETAIL_VIEW);
					this.eventDelegator(evt, GtmTrackingType.PRODUCT_IMPRESSION_VIEW);
					this.eventDelegator(evt, GtmTrackingType.PROMO_VIEWS);
					this.eventDelegator(evt, GtmTrackingType.PURCHASE);
					this.eventDelegator(evt, GtmTrackingType.BASKET_FREE_GIFT);
					this.eventDelegator(evt, GtmTrackingType.CHECKOUT_VIEW);
					this.eventDelegator(evt, GtmTrackingType.REGISTRATION_VIEW);
				}.bind(this),
			);

			window.od.events.subscribe(
				GtmTrackingType.ADD_TO_BASKET,
				function (data) {
					this.eventDelegator(data, GtmTrackingType.ADD_TO_BASKET);
				}.bind(this),
			);

			window.od.events.subscribe(
				GtmTrackingType.BUNDLE_ADD_TO_BASKET,
				function (data) {
					this.eventDelegator(data, GtmTrackingType.BUNDLE_ADD_TO_BASKET);
				}.bind(this),
			);

			window.od.events.subscribe(
				GtmTrackingType.REORDER_ADD_TO_BASKET,
				function (data) {
					this.eventDelegator(data, GtmTrackingType.REORDER_ADD_TO_BASKET);
				}.bind(this),
			);

			window.od.events.subscribe(
				'richRelevance_Loaded',
				function (data) {
					this.eventDelegator(data, 'richRelevance_Loaded');
				}.bind(this),
			);
		}
	}

	run() {
		if (this.dataLayerReady()) {
			this.addEventListener();
		} else if (this.dataLayerReadyCounter < this.dataLayerReadyMaxCount) {
			return this.wait(this.run);
		} else {
			this.logErrorToServer();
		}
	}

	dataLayerReady() {
		return (
			window.dataLayer instanceof Array &&
			typeof window.dataLayer[0] === 'object' &&
			window.dataLayer[0].navigation
		);
	}

	async wait(recursiveMethod) {
		this.dataLayerReadyCounter += 1;
		await new Promise(resolve =>
			setTimeout(() => {
				resolve(true);
			}, this.dataLayerReadyMilliseconds),
		);
		return recursiveMethod();
	}

	logErrorToServer() {
		const { events } = window.od;

		events.publish(Events.GLOBAL_JS_ERROR_REPORTED, {
			message: JSON.stringify({
				errorType: 'window.dataLayer[0] not populated correctly.',
				data: new Date(),
				dataLayer: window.dataLayer,
				signalData: window.signalData,
			}),
			lineno: 74,
			filename: 'gtmTrackingManager.js',
		});
	}

	eventDelegator(evt, type) {
		let clickableTarget;
		let delegator;
		let trackingData;

		if (!type) {
			clickableTarget = this.findClosestClickableElement(evt.target);
		}

		const eventType = clickableTarget
			? clickableTarget.getAttribute('data-gtm-tracking-type')
			: type;

		if (eventType) {
			delegator = this.delegators[eventType];
		}

		// Check to see whether link should open in new tab
		if (
			delegator &&
			clickableTarget &&
			this.checkIfOpeningInNewTab(clickableTarget)
		) {
			this.openLinkInNewTab(clickableTarget);
			delegator.openInNewTab = true;
		}

		if (delegator) {
			trackingData = delegator.getTrackingData(clickableTarget, evt);
		}

		if (trackingData) {
			this.pushToDataLayer(trackingData);
			this.preventDefault(evt, clickableTarget, delegator);
		}

		// Reset delegator property to not open in new tab
		if (delegator && 'openInNewTab' in delegator) {
			delegator.openInNewTab = false;
		}
	}

	checkIfOpeningInNewTab(clickableTarget) {
		if ('target' in clickableTarget) return clickableTarget.target === '_blank';
	}

	openLinkInNewTab(clickableTarget) {
		const newTab = window.open();
		newTab.opener = null;
		newTab.location = clickableTarget.href;
	}

	preventDefault(evt, clickableTarget, delegator) {
		if (delegator.preventDefault) {
			evt.preventDefault();
		}

		if (
			clickableTarget &&
			delegator.callback instanceof Function &&
			!delegator.openInNewTab
		) {
			this.startTimeout(delegator.callback.bind(delegator));
		}

		if (delegator.preventDefaultSync) {
			delegator.preventDefaultSyncCallback();
		}
	}

	generateDelegators() {
		GtmProductImpressionClick.isTest = this.isTest;
		return delegatorClassList.reduce((accumulator, delegatorInstance) => {
			accumulator[delegatorInstance.type] = delegatorInstance;
			return accumulator;
		}, {});
	}

	pushToDataLayer(trackingData) {
		if (trackingData instanceof Array) {
			trackingData.forEach(data => window.dataLayer.push(data));
		} else {
			window.dataLayer.push(trackingData);
		}
	}

	findClosestClickableElement(target) {
		return target.closest('a') || target.closest('button');
	}

	startTimeout(callback) {
		setTimeout(() => {
			if (!this.isTest) {
				callback();
			}
		}, this.setTimeoutCallbackTimer);
	}
}

const instance = new GtmTrackingManager();
export default () => instance;

export const GtmTrackingManagerClass = GtmTrackingManager;
